import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef
} from "react";
import {generateClient} from "aws-amplify/api";
import {useOktaAuth} from "@okta/okta-react";
import {useEnvironment} from "@common-core/react-runtime/context";
import {type V6Client} from "@aws-amplify/api-graphql";
import {configureAmplify} from "./amplifyConfig";
import {Environment} from "../runtime";

const AmplifyClientContext = createContext<V6Client | null>(null);

export const useAmplifyClient = (): V6Client | null => {
  return useContext(AmplifyClientContext);
};

export const AmplifyClientProvider: FC<PropsWithChildren> = ({children}) => {
  const environment = useEnvironment<Environment>();
  const {authState} = useOktaAuth();

  const latestAuthState = useRef(authState);
  useEffect(() => {
    latestAuthState.current = authState;
  }, [authState]);

  const client = useMemo(() => {
    configureAmplify({
      endpoint: environment.appsync,
      region: window.AWS_REGION ?? "us-east-1",
      getAccessToken: () =>
        latestAuthState.current?.accessToken?.accessToken ??
        authState?.accessToken?.accessToken,
      getIdToken: () =>
        latestAuthState.current?.idToken?.idToken ?? authState?.idToken?.idToken
    });

    return generateClient();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [environment.appsync, authState?.isAuthenticated]);

  return (
    <AmplifyClientContext.Provider value={client}>
      {children}
    </AmplifyClientContext.Provider>
  );
};
